import {
    Completion,
    CompletionContext,
    snippetCompletion,
} from "@codemirror/autocomplete";

import {
    LogSearchFields,
    LogSearchFunctions,
    LogSearchLogTypeCompletions,
    LogSearchOperators,
    LogSearchPlaceholders,
    LogSearchSnippets,
} from "./schema.js";

type CompletionToken = {
    from: number;
    text: string;
    quoted: boolean;
};

const WordChars = /[a-zA-Z0-9_.$:[\]*@]/;

function toCompletion(entry: {
    label: string;
    apply?: string;
    detail: string;
    info: string;
}, type: Completion["type"]): Completion {
    const completion = {
        label: entry.label,
        apply: entry.apply ?? entry.label,
        detail: entry.detail,
        info: entry.info,
        type,
    };

    return entry.apply?.includes("$")
        ? snippetCompletion(entry.apply, completion)
        : completion;
}

const FieldCompletions = LogSearchFields.map((field) => toCompletion(field, "property"));
const FunctionCompletions = LogSearchFunctions.map((fn) => toCompletion(fn, "function"));
const OperatorCompletions = LogSearchOperators.map((operator) => toCompletion(operator, operator.detail === "keyword" ? "keyword" : "operator"));
const PlaceholderCompletions = LogSearchPlaceholders.map((placeholder) => toCompletion(placeholder, "variable"));
const SnippetCompletions = LogSearchSnippets.map((snippet) => toCompletion(snippet, "keyword"));
const TypeCompletions = LogSearchLogTypeCompletions.map((logType) => toCompletion(logType, "constant"));

function findOpenStringStart(before: string) {
    let escaped = false;
    let openAt = -1;

    for (let index = 0; index < before.length; index++) {
        const char = before[index]!;

        if (escaped) {
            escaped = false;
            continue;
        }

        if (char === "\\") {
            escaped = true;
            continue;
        }

        if (char === "\"")
            openAt = openAt === -1 ? index : -1;
    }

    return openAt;
}

function getCompletionToken(context: CompletionContext): CompletionToken {
    const before = context.state.sliceDoc(0, context.pos);
    const openStringStart = findOpenStringStart(before);

    if (openStringStart !== -1) {
        return {
            from    : openStringStart + 1,
            text    : before.slice(openStringStart + 1),
            quoted  : true,
        };
    }

    let from = context.pos;

    while (from > 0 && WordChars.test(context.state.sliceDoc(from - 1, from)))
        from--;

    return {
        from,
        text    : context.state.sliceDoc(from, context.pos),
        quoted  : false,
    };
}

function getPrefix(context: CompletionContext, token: CompletionToken) {
    return context.state.sliceDoc(0, token.quoted ? token.from - 1 : token.from);
}

function tail(value: string) {
    return value.slice(Math.max(0, value.length - 240));
}

function isTypeValueContext(prefix: string) {
    const value = tail(prefix);

    return /(?:^|[\s(])type\s*(?:==|eq|!=|ne)\s*$/.test(value)
        || /(?:^|[\s(])type\s+(?:in|not\s+in)\s*\{\s*(?:(?:"[^"]*"\s*,\s*)*)$/.test(value);
}

function isGeneralExpressionContext(prefix: string, token: CompletionToken, explicit: boolean) {
    if (explicit || token.text.length > 0)
        return true;

    const value = tail(prefix);

    return value.trim() === ""
        || /(?:^|[\s(])(?:and|or|not)\s*$/.test(value)
        || /[({,]\s*$/.test(value);
}

function getTypeCompletions(context: CompletionContext, token: CompletionToken): Completion[] {
    if (!token.quoted)
        return TypeCompletions;

    const hasClosingQuote = context.state.sliceDoc(context.pos, context.pos + 1) === "\"";

    return LogSearchLogTypeCompletions.map((logType) => {
        const value = logType.label.slice(1, -1);

        return {
            label   : logType.label,
            apply   : hasClosingQuote ? value : `${value}"`,
            detail  : logType.detail,
            info    : logType.info,
            type    : "constant",
        };
    });
}

export function logSearchCompletionSource(context: CompletionContext) {
    const token = getCompletionToken(context);
    const prefix = getPrefix(context, token);

    if (isTypeValueContext(prefix)) {
        return {
            from        : token.from,
            options     : getTypeCompletions(context, token),
            validFor    : token.quoted ? /^[a-zA-Z0-9_]*$/ : /^"?[a-zA-Z0-9_]*"?$/,
            filter      : true,
        };
    }

    if (token.text.startsWith("@")) {
        return {
            from        : token.from,
            options     : PlaceholderCompletions,
            validFor    : /^@[a-zA-Z0-9_:"]*$/,
            filter      : true,
        };
    }

    if (!isGeneralExpressionContext(prefix, token, context.explicit))
        return null;

    return {
        from        : token.from,
        options     : [
            ...FieldCompletions,
            ...FunctionCompletions,
            ...OperatorCompletions,
            ...SnippetCompletions,
            ...PlaceholderCompletions,
        ],
        validFor    : /^@?[a-zA-Z0-9_.$:[\]*]*$/,
        filter      : true,
    };
}
